import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal as UiModal } from "../../../../components/ui/Modal";
import { Button } from "../../../../components/ui/Button";
import { Input } from "../../../../components/ui/Input";
import { Select } from "../../../../components/ui/Select";
import { Textarea } from "../../../../components/ui/Textarea";
import { FieldLabel } from "../../../../components/ui/FieldLabel";
import { createRole, updateRole, rolesErrorMessage } from "../../../../api/roles";
import { ROLE_KINDS } from "./rolesModel";

export const ROLE_NAME_MAX = 60;
export const ROLE_DESCRIPTION_MAX = 280;

/** Aviso de error dentro de un diálogo (role=alert para lectores de pantalla). */
export function InlineError({ children }) {
  if (!children) return null;
  return (
    <p role="alert" className="rounded-lg border border-status-error bg-status-error-soft px-3 py-2 text-sm text-status-error">
      {children}
    </p>
  );
}

/**
 * Diálogo de confirmación genérico. `error` se pinta adentro: el diálogo no se
 * cierra solo ante un fallo, para que el usuario lea el motivo.
 */
export function ConfirmModal({
  open, title, children, confirmLabel, confirmVariant = "primary", confirmDisabled = false,
  busy = false, error = "", onConfirm, onCancel,
}) {
  const { t } = useTranslation();
  return (
    <UiModal
      open={open}
      onClose={() => !busy && onCancel()}
      size="md"
      title={title}
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={busy}>{t("Cancelar")}</Button>
          <Button variant={confirmVariant} onClick={onConfirm} loading={busy} disabled={confirmDisabled}>
            {confirmLabel}
          </Button>
        </div>
      }
    >
      <div className="space-y-3 text-sm text-fg">
        {children}
        <InlineError>{error}</InlineError>
      </div>
    </UiModal>
  );
}

/**
 * Crear / duplicar / renombrar un rol propio.
 *  - create:    elige base (gestionable), nombre, descripción y si copia los permisos del base.
 *  - duplicate: base fijo (el del rol duplicado); copia sus permisos.
 *  - rename:    solo nombre y descripción (PATCH).
 * Al copiar solo se llevan los permisos que el que llama puede otorgar: el
 * backend rechazaría el resto con PERMISSION_NOT_GRANTABLE.
 */
export function RoleFormModal({ mode, open, roles, source, grantable, onClose, onDone }) {
  const { t } = useTranslation();
  const bases = roles.filter((r) => r.kind !== ROLE_KINDS.CUSTOM && r.editable);
  const [baseRoleId, setBaseRoleId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [copyPermissions, setCopyPermissions] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [nameError, setNameError] = useState("");

  useEffect(() => {
    if (!open) return;
    setBusy(false);
    setError("");
    setNameError("");
    setCopyPermissions(true);
    if (mode === "rename") {
      setBaseRoleId(source?.baseRoleId || "");
      setName(source?.name || "");
      setDescription(source?.description || "");
    } else if (mode === "duplicate") {
      setBaseRoleId(source?.baseRoleId || "");
      setName(t("{{name}} (copia)", { name: source?.name || "" }).slice(0, ROLE_NAME_MAX));
      setDescription(source?.description || "");
    } else {
      setBaseRoleId("");
      setName("");
      setDescription("");
    }
    // Se reinicia solo al abrir: un refetch de roles con el diálogo abierto no debe borrar lo escrito.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, source?.id]);

  const baseEntry = bases.find((r) => r.baseRoleId === baseRoleId);
  const copySource = mode === "duplicate" ? source : baseEntry;
  const copyable = (copySource?.permissions || []).filter((k) => grantable.has(k));
  const omitted = (copySource?.permissions || []).length - copyable.length;
  const withCopy = mode === "duplicate" || copyPermissions;

  const submit = async () => {
    if (busy) return;
    const trimmed = name.trim();
    if (!trimmed) { setNameError(t("Escribe un nombre para el rol.")); return; }
    if (trimmed.length > ROLE_NAME_MAX) { setNameError(t("Máximo {{max}} caracteres", { max: ROLE_NAME_MAX })); return; }
    if (mode !== "rename" && !baseRoleId) { setError(t("Elige el rol base.")); return; }
    setNameError("");
    setError("");
    setBusy(true);
    const desc = description.trim();
    let result;
    if (mode === "rename") {
      const patch = {};
      if (trimmed !== source.name) patch.name = trimmed;
      if (desc !== (source.description || "")) patch.description = desc;
      result = await updateRole(source.id, patch);
    } else {
      result = await createRole({
        name: trimmed,
        baseRoleId,
        ...(desc ? { description: desc } : {}),
        permissions: withCopy ? copyable : [],
      });
    }
    setBusy(false);
    if (result.error) {
      setError(rolesErrorMessage(result.error, t, t("No se pudo guardar el rol. Intenta de nuevo.")));
      return;
    }
    onDone(result.data);
  };

  const title = {
    create: t("Crear rol propio"),
    duplicate: t("Duplicar rol"),
    rename: t("Renombrar rol"),
  }[mode];

  return (
    <UiModal
      open={open}
      onClose={() => !busy && onClose()}
      size="md"
      title={title}
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>{t("Cancelar")}</Button>
          <Button onClick={submit} loading={busy}>
            {mode === "rename" ? t("Guardar") : t("Crear rol")}
          </Button>
        </div>
      }
    >
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); submit(); }} noValidate>
        {mode === "create" && (
          <Select
            label={t("Rol base")}
            value={baseRoleId}
            onChange={(e) => setBaseRoleId(e.target.value)}
            placeholder={t("Selecciona un rol base")}
            options={bases.map((r) => ({ value: r.baseRoleId, label: r.name }))}
          />
        )}
        {mode === "duplicate" && (
          <p className="text-sm text-fg-muted">
            {t("Se crea un rol propio sobre la base “{{base}}” con los mismos permisos.", { base: source?.name || "" })}
          </p>
        )}
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor="role-form-name">{t("Nombre")} <span className="text-status-error">*</span></FieldLabel>
          <Input
            id="role-form-name"
            value={name}
            maxLength={ROLE_NAME_MAX}
            invalid={!!nameError}
            aria-invalid={!!nameError}
            onChange={(e) => setName(e.target.value)}
          />
          {nameError ? <span className="text-xs text-status-error">{nameError}</span> : null}
        </div>
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor="role-form-description">{t("Descripción (opcional)")}</FieldLabel>
          <Textarea
            id="role-form-description"
            rows={3}
            value={description}
            maxLength={ROLE_DESCRIPTION_MAX}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        {mode === "create" && (
          <label className="flex items-start gap-2 text-sm text-fg">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-brand-600"
              checked={copyPermissions}
              onChange={(e) => setCopyPermissions(e.target.checked)}
            />
            <span>{t("Copiar los permisos actuales del rol base")}</span>
          </label>
        )}
        {mode !== "rename" && withCopy && omitted > 0 && copySource && (
          <p role="note" className="text-xs text-status-warning">
            {t("Se omitirán {{n}} permisos que no puedes otorgar.", { n: omitted })}
          </p>
        )}
        <InlineError>{error}</InlineError>
        {/* Enter dentro de un input envía el formulario sin depender del botón del footer. */}
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </UiModal>
  );
}
